import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { isMobile } from 'react-device-detect';
import { isEmpty } from 'lodash';
import { Disclosure } from '@headlessui/react';

// Components
import Card from '../../components/Card';
import StepTitle from '../../components/StepTitle';
import StepNavigation from '../../components/StepNavigation';
import FeedbackUploader from './FeedbackUploader';
import Button from '../../components/Button';
import NativeSelect from '../../components/fields/NativeSelect';
import Chip from '../../components/Chip';
import MailCatcher from '../MailDocumentCatcher/MailCatcherToken';
import DocumentExamples from './DocumentExamples';

// Hooks
import { useJsonApi } from '../../services/GlobalStore/GlobalStore';
import useAuthenticate from '../../hooks/useAuthenticate';
// Translations
import routes from '../../messages/routes';
import messages from './messages';
import Alert from '../../components/Alert';

// Icons
import PaxIcon from '../../assets/icons/pax-icon.inline.svg';
import { steps, useTranslatedNavigate } from '../../utils/routes';

import { getIdFromNumber } from '../ClaimPage/Claim';
import { DocsInfoPassengerStatus } from '../Account/ClaimDetails/ClaimDocuments/DocsInfoGroup';
import MailCatcherDescription from './MailCatcherDescription';
import { useDocumentsNavigate } from '../../hooks/useDocumentsNavigate';
import { PageType } from '../Passengers/PassengerContainer';

export enum RequirementType {
  Identification = 'identification',
  BookingDocs = 'booking_docs',
  BookingConfirmation = 'booking_confirmation',
  Authorization = 'authorization',
  DelayConfirmation = 'delay_confirmation',
  PaymentReceipt = 'payment_receipt',
  BirthCertification = 'birth_certificate',
  AdditionalCostAlternativeFlight = 'upload-additional-costs',
}

/**
 * Returns color from passenger docs status
 */
const mapPassengerStatusToChipColor = (
  status: string,
  updatedPassenger: boolean
): string | undefined => {
  if (['failed', 'pending'].includes(status) && !updatedPassenger)
    return 'danger';
  if (['complete', 'waiting_for_approval'].includes(status) || updatedPassenger)
    return 'success';
  return undefined;
};

const otherDocumentTypesOptions = [
  { value: '10', label: messages.documentTypeAirlineReaction },
  { value: '8', label: messages.documentTypeExtraCosts },
  { value: '94', label: messages.documentTypeBirthCertificate },
  { value: '97', label: messages.documentTypeAbandonedLetter },
  { value: '9', label: messages.documentTypeReleveDidentiteBancaire },
  { value: '12', label: messages.documentTypeOther },
];

// IMPORTANT !!!!
// updatedPassenger state is a temporary solution, needs to be changed ASAP

const DirectFeedbackUpload = ({
  pageType,
  categoryId,
  passengerId,
  claimNumber,
  documentType,
  withNavigation = false,
}: {
  pageType: PageType;
  categoryId: string;
  passengerId?: string;
  claimNumber: string;
  documentType?: RequirementType;
  withNavigation?: boolean;
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const translatedNavigate = useTranslatedNavigate();
  const { token } = useAuthenticate();
  const [reasons, setReasons] = useState([]);
  const [updatedPassenger, setUpdatedPassenger] = useState(false);
  const [updatedPassengerId, setUpdatedPassengerId] = useState(null);
  const claimId =
    localStorage.getItem('yo:claimId') || getIdFromNumber(claimNumber);

  const routeForRequirements = passengerId
    ? `passengers/${passengerId}`
    : `claims/${claimId}`;

  const { data: passengerRequirements, get: getRequirements } = useJsonApi(
    `${routeForRequirements}/requirements/${documentType}?include=passenger.person`,
    {
      credentials: 'include',
      cachePolicy: 'no-cache',
    }
  );

  const { data: documentExamples } = useJsonApi(
    `claims/${claimId}/documentExamples`,
    {
      queryParams: {
        include: ['file'],
      },
      credentials: 'include',
      cachePolicy: 'no-cache',
    },
    []
  );

  useEffect(() => {
    if (documentType) getRequirements();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (updatedPassenger) {
      getRequirements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedPassenger]);

  const { navigateNext, navigateBack } = useDocumentsNavigate({
    claimId,
    documentType,
    passengerId,
    pageType,
  });

  function navigateToDocumentsOverview(): Promise<void> {
    return translatedNavigate([
      '/',
      routes[steps.Account],
      routes[steps.Claim],
      claimNumber,
      routes[steps.ClaimDocuments],
    ]);
  }

  const isCompleted =
    !isEmpty(passengerRequirements) &&
    passengerRequirements.every(({ status }: any) =>
      ['complete', 'waiting_for_approval'].includes(status)
    );

  const showStatus = (isUpdated: boolean, status: string) => {
    return isUpdated
      ? formatMessage(messages?.waiting_for_approval)
      : formatMessage(messages[status]);
  };

  return (
    <Formik
      initialValues={{
        files: [],
        category: '',
      }}
      onSubmit={navigateNext}
    >
      {({ values }): JSX.Element => (
        <Form>
          <Card onlyDesktop={true}>
            <StepTitle>
              {documentType
                ? formatMessage(messages.uploadHeader, {
                    // eslint-disable-next-line
                    dark: (...chunks) => (
                      <span className="text-black">{chunks}</span>
                    ),
                    documentType,
                    passengerName: passengerId
                      ? `${passengerRequirements?.[0]?.passenger?.person?.first_name} ${passengerRequirements?.[0]?.passenger?.person?.last_name}`
                      : null,
                  })
                : formatMessage(messages.uploadAdditionalDocumentTitle)}
            </StepTitle>
            <Card onlyMobile={true}>
              {documentType ? (
                <>
                  <p className="m-0 mx-10 mb-20 sm:mx-0" data-testid="info">
                    {formatMessage(messages.description, {
                      documentType,
                      // eslint-disable-next-line react/no-multi-comp
                      strong: (...chunks) => <strong>{chunks}</strong>,
                    })}
                  </p>
                  {documentExamples &&
                  documentExamples?.length &&
                  categoryId === '5' ? (
                    <div className="bg-primary-05 mb-30 px-15 py-12">
                      <Disclosure>
                        {({ open }) => (
                          <DocumentExamples
                            open={open}
                            documentExamples={documentExamples}
                          />
                        )}
                      </Disclosure>
                    </div>
                  ) : null}
                </>
              ) : (
                <NativeSelect
                  className={'mb-15'}
                  name="category"
                  options={otherDocumentTypesOptions}
                  label={messages.labelTypeOfDocument}
                  placeholder={messages.placeholderTypeOfDocument}
                />
              )}
              <FeedbackUploader
                categoryId={categoryId || values.category}
                feedbackOff={!documentType}
                claimId={claimId}
                reasons={reasons}
                setReasons={setReasons}
                getRequirements={getRequirements}
                passengers={passengerRequirements}
                setUpdatedPassenger={setUpdatedPassenger}
                setUpdatedPassengerId={setUpdatedPassengerId}
                updatedPassenger={updatedPassenger}
              />
              {!isEmpty(reasons)
                ? Object.entries(reasons).map(([, reason]) => (
                    <Alert
                      level="warning"
                      className="mb-30"
                      key={`reason-${reason?.id}`}
                    >
                      {reason?.reason}
                    </Alert>
                  ))
                : null}
              {!isEmpty(passengerRequirements)
                ? passengerRequirements.map(
                    (requirement: DocsInfoPassengerStatus, index: number) => {
                      return (
                        <div
                          data-testid="passenger-row"
                          className={clsx(
                            'border rounded border-gray-50 flex flex-col bg-white',
                            {
                              'mt-15':
                                passengerRequirements.length > 1 && isMobile,
                              'border-b-0':
                                index !== passengerRequirements.length - 1 &&
                                !isMobile,
                            }
                          )}
                          key={`key-${requirement?.id}`}
                        >
                          <div
                            className={clsx(
                              'h-50 flex justify-between items-center'
                            )}
                          >
                            <PaxIcon className="mx-15 fill-primary" />
                            <span className="flex-1">{`${
                              isMobile
                                ? requirement?.passenger?.person?.first_name.split(
                                    ''
                                  )[0] +
                                  '. ' +
                                  requirement?.passenger?.person?.last_name
                                : requirement?.passenger?.person?.first_name +
                                  ' ' +
                                  requirement?.passenger?.person?.last_name
                            }`}</span>
                            <Chip
                              color={mapPassengerStatusToChipColor(
                                requirement?.status,
                                updatedPassengerId ===
                                  requirement?.passenger?.id
                              )}
                            >
                              {requirement?.status
                                ? showStatus(
                                    updatedPassengerId ===
                                      requirement?.passenger?.id,
                                    requirement?.status
                                  )
                                : null}
                            </Chip>
                            <span className="flex-1" />
                          </div>
                        </div>
                      );
                    }
                  )
                : null}
              {documentType === RequirementType.BookingConfirmation ? (
                <div className={'mt-30 p-20 bg-primary-05 mb-10 rounded'}>
                  <div className="bg-green">
                    <MailCatcherDescription />
                    <MailCatcher claimId={claimId} token={token} />
                  </div>
                </div>
              ) : null}
              {withNavigation ? null : (
                <div className="flex justify-center mt-20">
                  <Button
                    theme="primary"
                    type="button"
                    onClick={navigateToDocumentsOverview}
                  >
                    {formatMessage(messages.saveButtonText)}
                  </Button>
                </div>
              )}
            </Card>
          </Card>
          {withNavigation ? (
            <StepNavigation
              onBackClick={navigateBack}
              backButtonMessage={formatMessage(messages.backButtonText)}
              submitButtonMessage={formatMessage(messages.submitButtonText)}
              dataLayerPushOnBack={true}
              dataLayerPushOnContinue={true}
              step={steps.UploadId}
              isNotCompleted={!isCompleted && !updatedPassenger}
            >
              <Button theme="info" type="button" onClick={navigateNext}>
                {formatMessage(messages.laterButtonText)}
              </Button>
            </StepNavigation>
          ) : null}
        </Form>
      )}
    </Formik>
  );
};

export default DirectFeedbackUpload;
